/* eslint-disable react/no-danger */
import classnamesBind from 'classnames/bind';
import Link from '@concur/nui-widgets/Link';
import Title from '@concur/nui-widgets/Title';
import Text from '@concur/nui-widgets/Text';
import PropTypes from 'prop-types';
import React from 'react';
import { withFormatter, getLanguage } from '@concur/nui-intl-runtime';
import {
    withErrorBoundary, getDataCenter, compose,
} from '@concur/core-ui-shell';
import ChinaICP from './_ChinaICP';
import CookiePreferences from './_CookiePreferences';
import FooterText from './_FooterText';
import Logo from '../Logo/Logo';
import SapLogo from './_SapLogo';
import ServiceStatusLink from './_ServiceStatusLink';
import styles from './AppFooter.css';

const CHINA_DATA_CENTER = 'china';
const CSS_BLOCK = 'sapcnqr-app-footer';
const year = new Date().getFullYear();

const Footer = (props) => {
    const {
        country,
        dataCenterLocation,
        demo,
        formatter,
        isLoggedIn,
        lang = 'en',
        location: { pathname } = {},
        isOpusUser,
        /* eslint-disable no-unused-vars */
        lastSigninDateString,
        isTravelUser,
        controllerPrivacyStatementUrl,
        showOuttaskDevBar,
        partnerLogo,
        isGov,
        supportLink,
        useSmallViewportBreakpoints,
        isNuiHome,
        configOptions,
        /* eslint-enable no-unused-vars */
    } = props;

    const classnames = classnamesBind.bind(styles);

    const dataCenter = getDataCenter(dataCenterLocation);

    const showChinaLicense = dataCenter === CHINA_DATA_CENTER
        && (!pathname || (pathname.indexOf('/profile') === -1 && pathname.indexOf('/v2/forgot') === -1));

    const classes = classnames(
        CSS_BLOCK,
        {
            [`${CSS_BLOCK}--china`]: showChinaLicense,
        },
        {
            [`${CSS_BLOCK}--opus`]: isOpusUser,
        },
    );

    const sectionClasses = classnames(
        `${CSS_BLOCK}__section`,
    );

    const logoSectionClasses = classnames(
        `${CSS_BLOCK}__section`,
        `${CSS_BLOCK}__section--logo`,
    );

    const sapConcurLogoClasses = classnames(
        `${CSS_BLOCK}__sap-concur`,
    );

    const getCookieStatementUrl = () => {
        const supportedLangs = [
            'bg',
            'cs',
            'da',
            'de',
            'el',
            'es',
            'es-la',
            'en-gb',
            'fi',
            'fr',
            'fr-ca',
            'hr',
            'hu',
            'id',
            'it',
            'ja',
            'ko',
            'nl',
            'no',
            'ms',
            'pl',
            'pt-br',
            'ro',
            'ru',
            'sk',
            'sl',
            'sv',
            'th',
            'tr',
            'uk',
            'vi',
            'zh-cn',
            'zh-tw',
        ];

        const langUrl = supportedLangs.includes(getLanguage(lang)) ? getLanguage(lang) : 'en';
        const cdnUrl = dataCenter === CHINA_DATA_CENTER ? 'static.concurcdn.cn' : 'static.concursolutions.com';
        return `https://${cdnUrl}/about/legal/privacy/${langUrl}/`;
    };

    if (isLoggedIn) return null;

    const opusLogo = (
        <div className={classnames(
            `${CSS_BLOCK}__opus-logo`,
            {
                [`${CSS_BLOCK}__opus-logo--center`]: showChinaLicense,
            },
        )}
        >
            <Title headingLevel={5}>{formatter.formattedMessage({ id: 'CoreUI.opusBrandName' })}</Title>
            <Text>{formatter.formattedMessage({ id: 'CoreUI.opusBrandTag' })}</Text>
        </div>
    );

    const footerLogo = isOpusUser ? opusLogo : (
        <SapLogo className={classnames(`${CSS_BLOCK}__logo`)} />
    );

    const chinaLogo = isOpusUser ? opusLogo : (
        <Logo
            className={classnames(`${CSS_BLOCK}__logo`)}
            styles={styles}
            cssBlock={CSS_BLOCK}
            isOpusUser={isOpusUser}
        />
    );

    const footerText = (
        <FooterText cssBlock={CSS_BLOCK} styles={styles}>
            {isOpusUser
                ? formatter.formattedMessage({ id: 'CoreUI.opusCopyright', values: { year } })
                : formatter.formattedMessage({ id: 'CoreUI.copyright', values: { year } })}
        </FooterText>
    );

    return (
        <div className={classes} role="contentinfo" data-test="app-footer-container">
            {showChinaLicense ? (
                <>
                    <section className={logoSectionClasses} data-test="app-footer-logos">
                        <div className={sapConcurLogoClasses}>
                            {chinaLogo}
                        </div>
                    </section>
                    <section className={sectionClasses} data-test="app-footer-links">
                        <div className={classnames(`${CSS_BLOCK}__items`)}>
                            <ServiceStatusLink dataCenterLocation={dataCenterLocation} footerLink />

                            {formatter?.preferences?.lang && (
                                <CookiePreferences
                                    country={country}
                                    demo={demo}
                                    lang={lang}
                                    linkProps={{
                                        'data-test': 'cookie-preferences-link',
                                        className: classnames(`${CSS_BLOCK}__link`, `${CSS_BLOCK}__link--trustarc`),
                                    }}
                                />
                            )}
                            <Link
                                className={classnames(`${CSS_BLOCK}__link`)}
                                href={getCookieStatementUrl()}
                                data-analytics-json={'{"applicationName":"footer","elementPath":"cookie-statement-link","action":"click","elementType":"link"}'}
                                rel="noreferrer noopener"
                                target="_blank"
                            >
                                {formatter.formattedMessage({ id: 'CoreUI.cookieStatement' })}
                            </Link>
                        </div>
                        <div className={classnames(`${CSS_BLOCK}__items`)}>
                            {showChinaLicense && (
                                <ChinaICP styles={styles} cssBlock={CSS_BLOCK} />
                            )}
                            {footerText}
                        </div>
                    </section>
                </>
            ) : (
                <>
                    {
                        isOpusUser ? footerLogo : null
                    }
                    <section className={classnames(`${CSS_BLOCK}__content`)}>
                        {!isOpusUser && footerLogo}
                        <ServiceStatusLink dataCenterLocation={dataCenterLocation} footerLink />
                        {formatter?.preferences?.lang && (
                            <CookiePreferences
                                country={country}
                                demo={demo}
                                lang={lang}
                                linkProps={{
                                    'data-test': 'cookie-preferences-link',
                                    className: classnames(`${CSS_BLOCK}__link`, `${CSS_BLOCK}__link--trustarc`),
                                }}
                            />
                        )}
                        <Link
                            className={classnames(`${CSS_BLOCK}__link`)}
                            href={getCookieStatementUrl()}
                            data-analytics-json={'{"applicationName":"footer","elementPath":"cookie-statement-link","action":"click","elementType":"link"}'}
                            rel="noreferrer noopener"
                            target="_blank"
                        >
                            {formatter.formattedMessage({ id: 'CoreUI.cookieStatement' })}
                        </Link>
                    </section>
                    <section className={classnames(`${CSS_BLOCK}__content`, `${CSS_BLOCK}__content--right`)}>
                        {footerText}
                    </section>
                </>
            )}
        </div>
    );
};

Footer.displayName = 'Footer';

Footer.cssBlock = CSS_BLOCK;

Footer.propTypes = {
    controllerPrivacyStatementUrl: PropTypes.string,
    country: PropTypes.string,
    dataCenterLocation: PropTypes.string,
    demo: PropTypes.bool,
    isGov: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    isTravelUser: PropTypes.bool,
    lang: PropTypes.string,
    lastSigninDateString: PropTypes.string,
    location: PropTypes.object,
    partnerLogo: PropTypes.string,
    showOuttaskDevBar: PropTypes.bool,
    supportLink: PropTypes.string,
    useSmallViewportBreakpoints: PropTypes.bool,
    isOpusUser: PropTypes.bool,
    isNuiHome: PropTypes.bool,
    configOptions: PropTypes.object,
};

export default compose(
    withFormatter,
    withErrorBoundary,
)(Footer);
